import type { FetchBaseQueryError } from '@reduxjs/toolkit/query'
import type { RiskGroup } from '@/types/api/risk-groups'

import { TagTypes } from '@/types/api/tag-types'

import { baseApiSlice } from './base-api-slice'

// Combined result for parent-org scenarios (e.g. Apex) where we need every
// child IB's risk groups in one list. `ib_id` is the owning broker — consumers
// use it to label entries and scope downstream dropdowns (recipient, fees).
export interface RiskGroupWithIb extends RiskGroup {
  ib_id: string
}

const riskSlice = baseApiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getRiskGroups: builder.query<RiskGroup[], string>({
      query: (ibId) => ({
        url: `risk/${ibId}/group/`,
        method: 'GET',
      }),
      providesTags: [TagTypes.RiskGroup],
    }),

    // Fan out `risk/{ibId}/group/` for a list of IBs in parallel. Used by
    // the markup editor so an Apex admin sees every child IB's risk groups
    // in the combined picker — the per-IB endpoint stays the source of truth.
    getRiskGroupsForIbs: builder.query<RiskGroupWithIb[], string[]>({
      async queryFn(ibIds, _api, _extra, baseQuery) {
        if (ibIds.length === 0) return { data: [] }

        const responses = await Promise.all(
          ibIds.map(async (ibId) => {
            const res = await baseQuery({
              url: `risk/${ibId}/group/`,
              method: 'GET',
            })
            return { ibId, res }
          }),
        )

        // Dedupe by risk group `id`: parent-org risk groups surface on every
        // child IB's `/risk/{ibId}/group/` response, so without this an
        // Apex-shaped parent with N children renders the same group N times
        // in the picker. Keep the first occurrence and attribute it to the
        // IB that returned it.
        const combined: RiskGroupWithIb[] = []
        const seenIds = new Set<string>()
        let lastError: FetchBaseQueryError | undefined
        let successCount = 0
        for (const { ibId, res } of responses) {
          if (res.error) {
            lastError = res.error as FetchBaseQueryError
            continue
          }
          successCount++
          const groups = res.data as RiskGroup[] | undefined
          for (const g of groups ?? []) {
            if (seenIds.has(g.id)) continue
            seenIds.add(g.id)
            combined.push({ ...g, ib_id: ibId })
          }
        }

        // Bubble an error only when every IB failed. A mix of empty-success
        // and error responses still counts as partial success — surface the
        // (possibly empty) slice so a single flaky IB doesn't blank the picker.
        if (successCount === 0 && lastError) return { error: lastError }
        return { data: combined }
      },
      providesTags: [TagTypes.RiskGroup],
    }),
  }),
})

export const {
  endpoints: { getRiskGroups, getRiskGroupsForIbs },
  useGetRiskGroupsQuery,
  useGetRiskGroupsForIbsQuery,
} = riskSlice
